/**
 * Templates page - Reusable prompt templates with placeholders
 * Workflow 8.4 from CLAUDE.md
 */
import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fab,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
  Alert,
  FormControlLabel,
  Switch,
  Tabs,
  Tab,
  Snackbar,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PublicIcon from '@mui/icons-material/Public';
import LockIcon from '@mui/icons-material/Lock';
import ShareIcon from '@mui/icons-material/Share';
import SaveIcon from '@mui/icons-material/Save';

import { templatesApi } from '../../services/api';

interface Template {
  entityId: string;
  templateName: string;
  description: string;
  promptText: string;
  tags: string[];
  isPublic: boolean;
  placeholders?: Array<{
    name: string;
    description?: string;
    defaultValue?: string;
  }>;
  createdAt: string;
}

export default function TemplatesPage() {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [useDialogOpen, setUseDialogOpen] = useState(false);
  const [placeholderValues, setPlaceholderValues] = useState<Record<string, string>>({});
  const [editMode, setEditMode] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({ open: false, message: '' });
  const [activeTab, setActiveTab] = useState(0);

  // New/Edit template form state
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    content: '',
    description: '',
    tags: '',
    instructions: '',
    isPublic: false,
  });

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await templatesApi.list();
      setTemplates(result.data || []);
    } catch (err) {
      setError('Failed to load templates');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Extract placeholders from template content (format: {{placeholder}})
  const extractPlaceholders = (content: string): string[] => {
    const regex = /\{\{(\w+)\}\}/g;
    const matches: string[] = [];
    let match;
    while ((match = regex.exec(content)) !== null) {
      if (!matches.includes(match[1])) {
        matches.push(match[1]);
      }
    }
    return matches;
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const handleView = (template: Template) => {
    setSelectedTemplate(template);
    setDialogOpen(true);
  };

  const handleUseTemplate = (template: Template) => {
    setSelectedTemplate(template);
    const placeholders = extractPlaceholders(template.promptText);
    const values: Record<string, string> = {};
    placeholders.forEach((p) => {
      values[p] = template.placeholders?.find((ph) => ph.name === p)?.defaultValue || '';
    });
    setPlaceholderValues(values);
    setUseDialogOpen(true);
  };

  const generateFromTemplate = () => {
    if (!selectedTemplate) return '';
    let result = selectedTemplate.promptText;
    Object.entries(placeholderValues).forEach(([key, value]) => {
      result = result.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), value);
    });
    return result;
  };

  const handleDelete = async () => {
    if (!selectedTemplate) return;
    try {
      await templatesApi.delete(selectedTemplate.entityId);
      setTemplates(templates.filter((t) => t.entityId !== selectedTemplate.entityId));
      setDeleteDialogOpen(false);
      setDialogOpen(false);
    } catch (err) {
      setError('Failed to delete template');
      console.error(err);
    }
  };

  const handleCreate = async () => {
    try {
      const tags = newTemplate.tags.split(',').map((t) => t.trim()).filter(Boolean);
      await templatesApi.create({
        name: newTemplate.name,
        content: newTemplate.content,
        description: newTemplate.description,
        tags,
        instructions: newTemplate.instructions,
        isPublic: newTemplate.isPublic,
      });
      setCreateDialogOpen(false);
      setNewTemplate({ name: '', content: '', description: '', tags: '', instructions: '', isPublic: false });
      setSnackbar({ open: true, message: 'Template created successfully!' });
      fetchTemplates();
    } catch (err) {
      setError('Failed to create template');
      console.error(err);
    }
  };

  const handleEdit = (template: Template) => {
    setNewTemplate({
      name: template.templateName,
      content: template.promptText,
      description: template.description || '',
      tags: template.tags.join(', '),
      instructions: '',
      isPublic: template.isPublic || false,
    });
    setSelectedTemplate(template);
    setEditMode(true);
    setCreateDialogOpen(true);
  };

  const handleUpdate = async () => {
    if (!selectedTemplate) return;
    try {
      const tags = newTemplate.tags.split(',').map((t) => t.trim()).filter(Boolean);
      await templatesApi.update(selectedTemplate.entityId, {
        name: newTemplate.name,
        content: newTemplate.content,
        description: newTemplate.description,
        tags,
        instructions: newTemplate.instructions,
        isPublic: newTemplate.isPublic,
      });
      setCreateDialogOpen(false);
      setEditMode(false);
      setSelectedTemplate(null);
      setNewTemplate({ name: '', content: '', description: '', tags: '', instructions: '', isPublic: false });
      setSnackbar({ open: true, message: 'Template updated successfully!' });
      fetchTemplates();
    } catch (err) {
      setError('Failed to update template');
      console.error(err);
    }
  };

  const handleShare = (template: Template) => {
    const url = `${window.location.origin}/templates?id=${template.entityId}`;
    navigator.clipboard.writeText(url);
    setSnackbar({ open: true, message: 'Template link copied to clipboard!' });
  };

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
    setEditMode(false);
    setSelectedTemplate(null);
    setNewTemplate({ name: '', content: '', description: '', tags: '', instructions: '', isPublic: false });
  };

  // Get unique tags for filtering
  const allTags = [...new Set(templates.flatMap((t) => t.tags))];

  const filteredTemplates = templates.filter((t) => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      t.templateName.toLowerCase().includes(searchLower) ||
      t.description.toLowerCase().includes(searchLower) ||
      t.tags.some((tag) => tag.toLowerCase().includes(searchLower))
    );
  });

  // Filter by tab (My Templates vs Public)
  const displayTemplates = activeTab === 0 
    ? filteredTemplates.filter(t => !t.isPublic)
    : filteredTemplates.filter(t => t.isPublic);

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Templates
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Reusable prompt templates with customizable placeholders
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tabs for My Templates vs Public */}
      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2 }}>
        <Tab label="My Templates" />
        <Tab label="Public Templates" />
      </Tabs>

      {/* Search and Tags */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          placeholder="Search templates..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ minWidth: 300 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        {allTags.length > 0 && (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {allTags.slice(0, 5).map((tag) => (
              <Chip
                key={tag}
                label={tag}
                size="small"
                variant={search === tag ? 'filled' : 'outlined'}
                onClick={() => setSearch(search === tag ? '' : tag)}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* Templates Grid */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
          <CircularProgress />
        </Box>
      ) : displayTemplates.length === 0 ? (
        <Box sx={{ textAlign: 'center', p: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No templates found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {search ? 'Try a different search term' : 'Create your first template to get started'}
          </Typography>
          {!search && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
            >
              Create Template
            </Button>
          )}
        </Box>
      ) : (
        <Grid container spacing={3}>
          {displayTemplates.map((template) => {
            const placeholders = extractPlaceholders(template.promptText);
            return (
              <Grid item xs={12} sm={6} md={4} key={template.entityId}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: 'pointer',
                    '&:hover': { boxShadow: 4 },
                  }}
                  onClick={() => handleView(template)}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Typography variant="h6" noWrap sx={{ maxWidth: '80%' }}>
                        {template.templateName}
                      </Typography>
                      <Tooltip title={template.isPublic ? 'Public' : 'Private'}>
                        {template.isPublic ? (
                          <PublicIcon fontSize="small" color="primary" />
                        ) : (
                          <LockIcon fontSize="small" color="action" />
                        )}
                      </Tooltip>
                    </Box>
                    
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 2,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {template.description || 'No description'}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'monospace',
                        bgcolor: 'background.default',
                        p: 1,
                        borderRadius: 1,
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {template.promptText}
                    </Typography>

                    {placeholders.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          Placeholders:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                          {placeholders.map((p) => (
                            <Chip
                              key={p}
                              label={`{{${p}}}`}
                              size="small"
                              variant="outlined"
                              color="primary"
                            />
                          ))}
                        </Box>
                      </Box>
                    )}

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 2 }}>
                      {template.tags.slice(0, 3).map((tag) => (
                        <Chip key={tag} label={tag} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </CardContent>

                  <CardActions sx={{ justifyContent: 'space-between' }}>
                    <Box>
                      <Tooltip title="Use Template">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUseTemplate(template);
                          }}
                        >
                          <PlayArrowIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Copy">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopy(template.promptText);
                          }}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {template.isPublic && (
                        <Tooltip title="Share">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleShare(template);
                            }}
                          >
                            <ShareIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                    <Box>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(template);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* FAB */}
      <Fab
        color="primary"
        aria-label="add"
        sx={{ position: 'fixed', bottom: 24, right: 24 }}
        onClick={() => setCreateDialogOpen(true)}
      >
        <AddIcon />
      </Fab>

      {/* View/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{selectedTemplate?.templateName}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {selectedTemplate?.description}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={8}
            value={selectedTemplate?.promptText || ''}
            variant="outlined"
            InputProps={{ readOnly: true }}
            sx={{ fontFamily: 'monospace' }}
          />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 2 }}>
            {selectedTemplate?.tags.map((tag) => (
              <Chip key={tag} label={tag} size="small" />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setDeleteDialogOpen(true)}
          >
            Delete
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
          <Button
            variant="contained"
            startIcon={<PlayArrowIcon />}
            onClick={() => {
              setDialogOpen(false);
              if (selectedTemplate) handleUseTemplate(selectedTemplate);
            }}
          >
            Use Template
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Template</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete &quot;{selectedTemplate?.templateName}&quot;?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Use Template Dialog */}
      <Dialog
        open={useDialogOpen}
        onClose={() => setUseDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Use Template: {selectedTemplate?.templateName}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Fill in the placeholder values to generate your prompt
          </Typography>

          {Object.keys(placeholderValues).map((placeholder) => (
            <TextField
              key={placeholder}
              fullWidth
              label={placeholder}
              value={placeholderValues[placeholder]}
              onChange={(e) =>
                setPlaceholderValues({ ...placeholderValues, [placeholder]: e.target.value })
              }
              sx={{ mb: 2 }}
              helperText={
                selectedTemplate?.placeholders?.find((p) => p.name === placeholder)?.description
              }
            />
          ))}

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            Generated Prompt:
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={6}
            value={generateFromTemplate()}
            variant="outlined"
            InputProps={{ readOnly: true }}
            sx={{ fontFamily: 'monospace' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUseDialogOpen(false)}>Cancel</Button>
          <Button
            startIcon={<ContentCopyIcon />}
            onClick={() => handleCopy(generateFromTemplate())}
          >
            Copy
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              window.location.href = `/optimizer?prompt=${encodeURIComponent(generateFromTemplate())}`;
            }}
          >
            Optimize
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create/Edit Template Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={handleCloseCreateDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{editMode ? 'Edit Template' : 'Create New Template'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Use {'{{placeholder}}'} syntax for variable parts of your template
          </Typography>
          
          <TextField
            fullWidth
            label="Name"
            value={newTemplate.name}
            onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Description"
            value={newTemplate.description}
            onChange={(e) => setNewTemplate({ ...newTemplate, description: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            multiline
            rows={6}
            label="Template Content"
            value={newTemplate.content}
            onChange={(e) => setNewTemplate({ ...newTemplate, content: e.target.value })}
            sx={{ mb: 2 }}
            placeholder="Write a {{tone}} email to {{recipient}} about {{topic}}"
          />
          <TextField
            fullWidth
            label="Tags (comma-separated)"
            value={newTemplate.tags}
            onChange={(e) => setNewTemplate({ ...newTemplate, tags: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Usage Instructions (optional)"
            value={newTemplate.instructions}
            onChange={(e) => setNewTemplate({ ...newTemplate, instructions: e.target.value })}
            sx={{ mb: 2 }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={newTemplate.isPublic}
                onChange={(e) => setNewTemplate({ ...newTemplate, isPublic: e.target.checked })}
              />
            }
            label="Make this template public (shareable)"
          />

          {newTemplate.content && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Detected placeholders:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {extractPlaceholders(newTemplate.content).map((p) => (
                  <Chip key={p} label={`{{${p}}}`} size="small" color="primary" />
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={editMode ? <SaveIcon /> : <AddIcon />}
            onClick={editMode ? handleUpdate : handleCreate}
            disabled={!newTemplate.name || !newTemplate.content}
          >
            {editMode ? 'Save Changes' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Box>
  );
}
